import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Target, 
  Edit, 
  Trash2, 
  Eye, 
  AlertCircle,
  Search,
  Filter,
  ChevronDown,
  PlusCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const ManageProjects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('ngoToken');
        const userType = localStorage.getItem('userType');
        const ngoUser = JSON.parse(localStorage.getItem('ngoUser') || '{}');
        
        if (!token || userType !== 'ngo') {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        // Primary endpoint for fetching projects
        const primaryEndpoint = 'http://localhost:3011/api/projects';
        
        try {
          console.log(`Fetching projects from: ${primaryEndpoint}`);
          console.log('Using token:', `Bearer ${token}`);
          
          const response = await axios.get(primaryEndpoint, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            timeout: 10000 // 10 second timeout
          });
          
          console.log('API response:', response);
          
          if (response.status === 200) {
            // The API returns an object with ngo, projects, and count properties
            const projectData = response.data;
            console.log('Projects data received:', projectData);
            
            // Set projects even if it's an empty array
            setProjects(Array.isArray(projectData.projects) ? projectData.projects : []);
            
            // No need for toast messages - they're distracting
            setLoading(false);
            return;
          } else {
            console.warn('Unexpected response status:', response.status);
            setError('Unexpected response from server');
          }
        } catch (err) {
          console.error('Error fetching projects:', err);
          
          if (err.response) {
            // The request was made and the server responded with an error status
            console.error('Error response:', err.response.data);
            if (err.response.status === 401) {
              setError('Authentication failed. Please log in again.');
            } else {
              setError('Failed to load projects. ' + (err.response.data.msg || ''));
            }
          } else if (err.request) {
            // The request was made but no response was received
            console.error('No response received');
            setError('Server not responding. Please try again later.');
          } else {
            // Something happened in setting up the request
            setError('Error preparing request: ' + err.message);
          }
        }
        
        // Don't use mock data anymore - just show empty state
        console.warn('Failed to fetch projects from server');
        setProjects([]);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setError('Failed to load projects');
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Filter projects based on status and search term
  const filteredProjects = projects.filter(project => {
    const matchesFilter = filter === 'all' || project.status === filter;
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleDeleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        setLoading(true);
        const token = localStorage.getItem('ngoToken');
        
        if (!token) {
          toast.error('Authentication required');
          setLoading(false);
          return;
        }
        
        console.log(`Attempting to delete project with ID: ${id}`);
        
        // Primary endpoint - try this first
        const primaryEndpoint = `http://localhost:3011/api/projects/${id}`;
        
        try {
          console.log(`Sending delete request to: ${primaryEndpoint}`);
          console.log('Authorization token:', `Bearer ${token}`);
          
          const response = await axios.delete(primaryEndpoint, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            timeout: 15000 // 15 second timeout for deletion
          });
          
          console.log('Delete response:', response);
          
          if (response.status === 200 && response.data && response.data.success) {
            console.log('Project deleted successfully from backend');
            // Update local state
            setProjects(prevProjects => prevProjects.filter(project => project._id !== id));
            toast.success('Project deleted successfully');
          } else {
            console.warn('Unexpected response format:', response.data);
            toast.error('Unexpected response from server');
          }
        } catch (error) {
          console.error('Error deleting project:', error);
          
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.error('Error response data:', error.response.data);
            console.error('Error response status:', error.response.status);
            
            if (error.response.status === 401) {
              toast.error('Not authorized to delete this project');
            } else if (error.response.status === 404) {
              toast.error('Project not found');
              // Remove from UI anyway if not found on server
              setProjects(prevProjects => prevProjects.filter(project => project._id !== id));
            } else {
              toast.error(`Server error: ${error.response.data.msg || 'Unknown error'}`);
            }
          } else if (error.request) {
            // The request was made but no response was received
            console.error('No response received:', error.request);
            toast.error('No response from server. Please try again later.');
          } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Error setting up request:', error.message);
            toast.error(`Error: ${error.message}`);
          }
        }
      } catch (error) {
        console.error('Unexpected error in delete handler:', error);
        toast.error('An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
            <Target className="mr-2" size={24} />
            Manage Projects
          </h1>
          <Link 
            to="/ngo/dashboard/projects/create" 
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center"
          >
            <PlusCircle size={18} className="mr-1" />
            Create New Project
          </Link>
        </div>
        
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-md">
          <div className="flex items-center">
            <AlertCircle className="text-red-500 mr-3" size={24} />
            <div>
              <p className="text-red-700 font-medium">Unable to load projects</p>
              <p className="text-red-600">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-2 text-red-700 hover:text-red-900 font-medium flex items-center"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <div className="flex justify-center mb-4">
            <AlertCircle size={48} className="text-gray-400" />
          </div>
          <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300 mb-2">No campaigns found</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            You haven't created any projects yet or there was an error loading them.
          </p>
          <Link 
            to="/ngo/dashboard/projects/create" 
            className="inline-flex items-center text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5"
          >
            <PlusCircle size={16} className="mr-2" />
            Create Your First Project
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
          <Target className="mr-2" size={24} />
          Manage Projects
        </h1>
        <Link 
          to="/ngo/dashboard/projects/create" 
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center"
        >
          <PlusCircle size={18} className="mr-1" />
          Create New Project
        </Link>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center">
          <span className="mr-2 text-gray-700 dark:text-gray-300">
            <Filter size={18} />
          </span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="all">All Projects</option>
            <option value="pending">Pending Approval</option>
            <option value="active">Active</option>
            <option value="draft">Drafts</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div className="relative w-full md:w-64">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search size={18} className="text-gray-500 dark:text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search projects..."
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <div className="flex justify-center mb-4">
            <AlertCircle size={48} className="text-gray-400" />
          </div>
          <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300 mb-2">No Campaigns Found</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {filter !== 'all' 
              ? `You don't have any ${filter} campaigns yet.` 
              : searchTerm 
                ? `No campaigns match your search "${searchTerm}".` 
                : "You haven't created any campaigns yet."}
          </p>
          <Link 
            to="/ngo/dashboard/projects/create" 
            className="inline-flex items-center text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5"
          >
            <PlusCircle size={16} className="mr-2" />
            Create Your First Campaign
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredProjects.map(project => (
            <div key={project._id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/4 h-48 md:h-auto overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4 md:w-3/4 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-1">
                          {project.title}
                        </h3>
                        <div className="flex items-center mb-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            project.status === 'active' ? 'bg-green-100 text-green-800' :
                            project.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => navigate(`/ngo/dashboard/projects/edit/${project._id}`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                          title="Edit Project"
                        >
                          <Edit size={18} />
                        </button>
                        <button 
                          onClick={() => handleDeleteProject(project._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                          title="Delete Project"
                        >
                          <Trash2 size={18} />
                        </button>
                        <Link 
                          to={`/ngo/dashboard/projects/${project._id}`}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-full"
                          title="View Project Details"
                        >
                          <Eye size={18} />
                        </Link>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2">
                      {project.description}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Category:</span>
                        <span className="ml-1">{project.category}</span>
                      </div>
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Location:</span>
                        <span className="ml-1">{project.location}</span>
                      </div>
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Start Date:</span>
                        <span className="ml-1">{new Date(project.startDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <span className="font-medium">End Date:</span>
                        <span className="ml-1">{new Date(project.endDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  {project.goalType === 'Fundraising' && (
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600 dark:text-gray-400">Raised: ₹{project.raisedAmount.toLocaleString()}</span>
                        <span className="text-gray-600 dark:text-gray-400">Goal: ₹{project.goalAmount.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(100, (project.raisedAmount / project.goalAmount) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageProjects;
